import { useEffect, useState, type ReactNode } from "react";
import { Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  Input,
  Select,
  Textarea,
} from "@/components/ui";
import { ApprovalPickerField } from "@/modules/approvals";
import { formatLabel, toIsoUtc } from "../labels";
import type {
  AppealSourceType,
  DataClassification,
  EnforcementActionType,
  ModerationEvidenceType,
  ModerationPolicyMappingType,
  ModerationSeverity,
  ReportType,
  SubjectRole,
} from "../types";

const REPORT_TYPES: ReportType[] = [
  "reported_post",
  "reported_user",
  "reported_community",
  "reported_business",
  "booking_concern",
  "future_governed_type",
];

const SEVERITIES: ModerationSeverity[] = ["low", "medium", "high", "critical"];

const EVIDENCE_TYPES: ModerationEvidenceType[] = [
  "report",
  "file",
  "post",
  "user_activity",
  "support_context",
  "external_reference",
  "future_governed_type",
];

const MAPPING_TYPES: ModerationPolicyMappingType[] = [
  "primary",
  "supporting",
  "exception",
  "future_governed_type",
];

const ENFORCEMENT_TYPES: EnforcementActionType[] = [
  "no_action",
  "content_removal",
  "warning",
  "capability_restriction",
  "temporary_suspension",
  "permanent_ban",
  "community_restriction",
  "escalation_to_senior_review",
];

const SUBJECT_ROLES: SubjectRole[] = [
  "primary",
  "secondary",
  "reporter",
  "subject",
  "affected_party",
  "beneficiary",
  "future_governed_type",
];

const CLASSIFICATIONS: DataClassification[] = [
  "public",
  "internal",
  "confidential",
  "strictly_confidential",
  "regulated",
];

const APPEAL_SOURCES: AppealSourceType[] = [
  "enforcement_action",
  "moderation_case",
  "future_governed_type",
];

export function ConfirmActionDialog({
  open,
  title,
  notice,
  actions,
  requireReason = true,
  requireApproval = false,
  approvalLabel = "Approval ID (when required)",
  extraFields,
  confirmVariant = "primary",
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  title: string;
  notice?: string;
  actions: string[];
  requireReason?: boolean;
  requireApproval?: boolean;
  approvalLabel?: string;
  extraFields?: ReactNode;
  confirmVariant?: "primary" | "danger";
  onClose: () => void;
  onSubmit: (input: {
    action: string;
    reason?: string;
    approval_id?: string;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const first = actions[0] ?? "";
  const [action, setAction] = useState(first);
  const [reason, setReason] = useState("");
  const [approvalId, setApprovalId] = useState("");

  useEffect(() => {
    if (!open) return;
    setAction(first);
    setReason("");
    setApprovalId("");
  }, [open, first]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={confirmVariant}
            loading={busy}
            disabled={
              !action ||
              (requireReason && reason.trim().length < 3) ||
              (requireApproval && approvalId.trim().length === 0)
            }
            onClick={() =>
              void onSubmit({
                action,
                ...(reason.trim() ? { reason: reason.trim() } : {}),
                ...(approvalId.trim()
                  ? { approval_id: approvalId.trim() }
                  : {}),
              })
            }
          >
            Confirm {formatLabel(action)}
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        {notice ? (
          <Text role="small" color="secondary" as="p">
            {notice}
          </Text>
        ) : null}
        <Select
          label="Action"
          value={action}
          onChange={(event) => setAction(event.target.value)}
          options={actions.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        {extraFields}
        <Textarea
          label={requireReason ? "Reason / rationale" : "Reason (optional)"}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        <ApprovalPickerField
          label={approvalLabel}
          value={approvalId}
          onChange={setApprovalId}
          required={requireApproval}
        />
        {error ? (
          <Alert tone="danger" title="Action failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function CreateReportDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    subject_type: string;
    subject_id: string;
    report_type: ReportType;
    reporter_user_id?: string | null;
    evidence_reference?: string | null;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [subjectType, setSubjectType] = useState("user");
  const [subjectId, setSubjectId] = useState("");
  const [reportType, setReportType] = useState<ReportType>("reported_user");
  const [reporterUserId, setReporterUserId] = useState("");
  const [evidenceReference, setEvidenceReference] = useState("");

  useEffect(() => {
    if (!open) return;
    setSubjectType("user");
    setSubjectId("");
    setReportType("reported_user");
    setReporterUserId("");
    setEvidenceReference("");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Create Report"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={
              subjectType.trim().length === 0 || subjectId.trim().length === 0
            }
            onClick={() =>
              void onSubmit({
                subject_type: subjectType.trim(),
                subject_id: subjectId.trim(),
                report_type: reportType,
                ...(reporterUserId.trim()
                  ? { reporter_user_id: reporterUserId.trim() }
                  : {}),
                ...(evidenceReference.trim()
                  ? { evidence_reference: evidenceReference.trim() }
                  : {}),
              })
            }
          >
            Create
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Reporter identity is a handling constraint. Do not expose protected
          reporter details beyond authorized case work. Reported Posts, Users,
          and Communities are subject views — not separate case models.
        </Text>
        <Select
          label="Report type"
          value={reportType}
          onChange={(event) =>
            setReportType(event.target.value as ReportType)
          }
          options={REPORT_TYPES.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        <Input
          label="Subject type"
          value={subjectType}
          onChange={(event) => setSubjectType(event.target.value)}
        />
        <Input
          label="Subject ID"
          value={subjectId}
          onChange={(event) => setSubjectId(event.target.value)}
        />
        <Input
          label="Reporter User ID (optional)"
          value={reporterUserId}
          onChange={(event) => setReporterUserId(event.target.value)}
        />
        <Input
          label="Evidence reference (optional)"
          value={evidenceReference}
          onChange={(event) => setEvidenceReference(event.target.value)}
        />
        {error ? (
          <Alert tone="danger" title="Create failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function ReportLinkOpenDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    reason: string;
    open_case?: boolean;
    moderation_case_id?: string;
    severity?: ModerationSeverity;
    market_id?: string | null;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [reason, setReason] = useState("");
  const [openCase, setOpenCase] = useState(true);
  const [caseId, setCaseId] = useState("");
  const [severity, setSeverity] = useState<ModerationSeverity>("medium");
  const [marketId, setMarketId] = useState("");

  useEffect(() => {
    if (!open) return;
    setReason("");
    setOpenCase(true);
    setCaseId("");
    setSeverity("medium");
    setMarketId("");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Link or open Moderation Case"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={
              reason.trim().length < 3 ||
              (!openCase && caseId.trim().length === 0)
            }
            onClick={() =>
              void onSubmit({
                reason: reason.trim(),
                ...(openCase
                  ? {
                      open_case: true,
                      severity,
                      ...(marketId.trim()
                        ? { market_id: marketId.trim() }
                        : {}),
                    }
                  : { moderation_case_id: caseId.trim() }),
              })
            }
          >
            Confirm
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Link an existing case or open a new one. Support Tickets stay separate
          from Moderation Cases.
        </Text>
        <Checkbox
          label="Open a new Moderation Case"
          checked={openCase}
          onChange={(event) => setOpenCase(event.target.checked)}
        />
        {openCase ? (
          <>
            <Select
              label="Severity"
              value={severity}
              onChange={(event) =>
                setSeverity(event.target.value as ModerationSeverity)
              }
              options={SEVERITIES.map((item) => ({
                value: item,
                label: formatLabel(item),
              }))}
            />
            <Input
              label="Market ID (optional)"
              value={marketId}
              onChange={(event) => setMarketId(event.target.value)}
            />
          </>
        ) : (
          <Input
            label="Existing Moderation Case ID"
            value={caseId}
            onChange={(event) => setCaseId(event.target.value)}
          />
        )}
        <Textarea
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        {error ? (
          <Alert tone="danger" title="Action failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function CreateCaseDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    severity?: ModerationSeverity;
    owner_user_id?: string | null;
    market_id?: string | null;
    decision_summary?: string | null;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [severity, setSeverity] = useState<ModerationSeverity>("medium");
  const [ownerUserId, setOwnerUserId] = useState("");
  const [marketId, setMarketId] = useState("");
  const [summary, setSummary] = useState("");

  useEffect(() => {
    if (!open) return;
    setSeverity("medium");
    setOwnerUserId("");
    setMarketId("");
    setSummary("");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Open Moderation Case"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            onClick={() =>
              void onSubmit({
                severity,
                ...(ownerUserId.trim()
                  ? { owner_user_id: ownerUserId.trim() }
                  : {}),
                ...(marketId.trim() ? { market_id: marketId.trim() } : {}),
                ...(summary.trim() ? { decision_summary: summary.trim() } : {}),
              })
            }
          >
            Open case
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Case states: new → triage → investigating → awaiting information →
          actioned → appealed → closed. This does not change Community
          ownership or membership.
        </Text>
        <Select
          label="Severity"
          value={severity}
          onChange={(event) =>
            setSeverity(event.target.value as ModerationSeverity)
          }
          options={SEVERITIES.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        <Input
          label="Owner User ID (optional)"
          value={ownerUserId}
          onChange={(event) => setOwnerUserId(event.target.value)}
        />
        <Input
          label="Market ID (optional)"
          value={marketId}
          onChange={(event) => setMarketId(event.target.value)}
        />
        <Textarea
          label="Decision summary (optional)"
          value={summary}
          onChange={(event) => setSummary(event.target.value)}
        />
        {error ? (
          <Alert tone="danger" title="Create failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function AddSubjectDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    subject_type: string;
    subject_id: string;
    subject_role: SubjectRole;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [subjectType, setSubjectType] = useState("user");
  const [subjectId, setSubjectId] = useState("");
  const [role, setRole] = useState<SubjectRole>("subject");

  useEffect(() => {
    if (!open) return;
    setSubjectType("user");
    setSubjectId("");
    setRole("subject");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add governed subject"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={
              subjectType.trim().length === 0 || subjectId.trim().length === 0
            }
            onClick={() =>
              void onSubmit({
                subject_type: subjectType.trim(),
                subject_id: subjectId.trim(),
                subject_role: role,
              })
            }
          >
            Add
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Input
          label="Subject type"
          value={subjectType}
          onChange={(event) => setSubjectType(event.target.value)}
        />
        <Input
          label="Subject ID"
          value={subjectId}
          onChange={(event) => setSubjectId(event.target.value)}
        />
        <Select
          label="Subject role"
          value={role}
          onChange={(event) => setRole(event.target.value as SubjectRole)}
          options={SUBJECT_ROLES.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        {error ? (
          <Alert tone="danger" title="Add failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function AddEvidenceDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    evidence_type: ModerationEvidenceType;
    file_asset_id?: string;
    source_type?: string;
    source_id?: string;
    classification?: DataClassification;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [evidenceType, setEvidenceType] =
    useState<ModerationEvidenceType>("file");
  const [fileAssetId, setFileAssetId] = useState("");
  const [sourceType, setSourceType] = useState("");
  const [sourceId, setSourceId] = useState("");
  const [classification, setClassification] =
    useState<DataClassification>("confidential");

  useEffect(() => {
    if (!open) return;
    setEvidenceType("file");
    setFileAssetId("");
    setSourceType("");
    setSourceId("");
    setClassification("confidential");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add evidence"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={
              fileAssetId.trim().length === 0 && sourceId.trim().length === 0
            }
            onClick={() =>
              void onSubmit({
                evidence_type: evidenceType,
                ...(fileAssetId.trim()
                  ? { file_asset_id: fileAssetId.trim() }
                  : {}),
                ...(sourceType.trim()
                  ? { source_type: sourceType.trim() }
                  : {}),
                ...(sourceId.trim() ? { source_id: sourceId.trim() } : {}),
                classification,
              })
            }
          >
            Add
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Evidence access is classification-controlled and minimum necessary.
          Provide a File Asset ID and/or a source ID.
        </Text>
        <Select
          label="Evidence type"
          value={evidenceType}
          onChange={(event) =>
            setEvidenceType(event.target.value as ModerationEvidenceType)
          }
          options={EVIDENCE_TYPES.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        <Input
          label="File Asset ID"
          value={fileAssetId}
          onChange={(event) => setFileAssetId(event.target.value)}
        />
        <Input
          label="Source type (optional)"
          value={sourceType}
          onChange={(event) => setSourceType(event.target.value)}
        />
        <Input
          label="Source ID (optional)"
          value={sourceId}
          onChange={(event) => setSourceId(event.target.value)}
        />
        <Select
          label="Classification"
          value={classification}
          onChange={(event) =>
            setClassification(event.target.value as DataClassification)
          }
          options={CLASSIFICATIONS.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        {error ? (
          <Alert tone="danger" title="Add failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function MapPolicyDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    policy_id: string;
    policy_version_id?: string | null;
    mapping_type?: ModerationPolicyMappingType;
    high_risk_policy_override?: boolean;
    approval_id?: string;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [policyId, setPolicyId] = useState("");
  const [versionId, setVersionId] = useState("");
  const [mappingType, setMappingType] =
    useState<ModerationPolicyMappingType>("primary");
  const [override, setOverride] = useState(false);
  const [approvalId, setApprovalId] = useState("");

  useEffect(() => {
    if (!open) return;
    setPolicyId("");
    setVersionId("");
    setMappingType("primary");
    setOverride(false);
    setApprovalId("");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Map Policy"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={
              policyId.trim().length === 0 ||
              (override && approvalId.trim().length === 0)
            }
            onClick={() =>
              void onSubmit({
                policy_id: policyId.trim(),
                ...(versionId.trim()
                  ? { policy_version_id: versionId.trim() }
                  : {}),
                mapping_type: mappingType,
                ...(override
                  ? {
                      high_risk_policy_override: true,
                      approval_id: approvalId.trim(),
                    }
                  : {}),
              })
            }
          >
            Map
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          High-risk Policy override requires Super Admin Approval. Marketing
          never sees private moderation evidence.
        </Text>
        <Input
          label="Policy ID"
          value={policyId}
          onChange={(event) => setPolicyId(event.target.value)}
        />
        <Input
          label="Policy Version ID (optional)"
          value={versionId}
          onChange={(event) => setVersionId(event.target.value)}
        />
        <Select
          label="Mapping type"
          value={mappingType}
          onChange={(event) =>
            setMappingType(event.target.value as ModerationPolicyMappingType)
          }
          options={MAPPING_TYPES.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        <Checkbox
          label="High-risk policy override (Super Admin)"
          checked={override}
          onChange={(event) => setOverride(event.target.checked)}
        />
        {override ? (
          <ApprovalPickerField
            label="Super Admin approved Approval (required)"
            value={approvalId}
            onChange={setApprovalId}
            required
            minAuthority="super_admin"
          />
        ) : null}
        {error ? (
          <Alert tone="danger" title="Map failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function ProposeEnforcementDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    subject_type: string;
    subject_id: string;
    action_type: EnforcementActionType;
    effective_from?: string;
    effective_to?: string | null;
    reason?: string | null;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [subjectType, setSubjectType] = useState("user");
  const [subjectId, setSubjectId] = useState("");
  const [actionType, setActionType] =
    useState<EnforcementActionType>("warning");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [effectiveTo, setEffectiveTo] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!open) return;
    setSubjectType("user");
    setSubjectId("");
    setActionType("warning");
    setEffectiveFrom("");
    setEffectiveTo("");
    setReason("");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Propose Enforcement Action"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={
              subjectType.trim().length === 0 ||
              subjectId.trim().length === 0 ||
              reason.trim().length < 1
            }
            onClick={() =>
              void onSubmit({
                subject_type: subjectType.trim(),
                subject_id: subjectId.trim(),
                action_type: actionType,
                ...(effectiveFrom.trim()
                  ? { effective_from: toIsoUtc(effectiveFrom) }
                  : {}),
                ...(effectiveTo.trim()
                  ? { effective_to: toIsoUtc(effectiveTo) }
                  : {}),
                reason: reason.trim(),
              })
            }
          >
            Propose
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Only API-supported action types. Permanent ban and high-impact actions
          need Dual Approval before activate. This does not change Community
          ownership or membership directly.
        </Text>
        <Select
          label="Action type"
          value={actionType}
          onChange={(event) =>
            setActionType(event.target.value as EnforcementActionType)
          }
          options={ENFORCEMENT_TYPES.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        <Input
          label="Subject type"
          value={subjectType}
          onChange={(event) => setSubjectType(event.target.value)}
        />
        <Input
          label="Subject ID"
          value={subjectId}
          onChange={(event) => setSubjectId(event.target.value)}
        />
        <Input
          label="Effective from (optional)"
          type="datetime-local"
          value={effectiveFrom}
          onChange={(event) => setEffectiveFrom(event.target.value)}
        />
        <Input
          label="Effective to (optional)"
          type="datetime-local"
          value={effectiveTo}
          onChange={(event) => setEffectiveTo(event.target.value)}
        />
        <Textarea
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        {error ? (
          <Alert tone="danger" title="Propose failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function EnforcementLifecycleDialog({
  open,
  action,
  actionType,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  action: string | null;
  actionType: EnforcementActionType | null;
  onClose: () => void;
  onSubmit: (input: {
    reason: string;
    approval_id?: string;
    high_impact?: boolean;
    high_risk_policy_override?: boolean;
    effective_to?: string | null;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [reason, setReason] = useState("");
  const [approvalId, setApprovalId] = useState("");
  const [highImpact, setHighImpact] = useState(false);
  const [override, setOverride] = useState(false);
  const [effectiveTo, setEffectiveTo] = useState("");

  useEffect(() => {
    if (!open) return;
    setReason("");
    setApprovalId("");
    setHighImpact(actionType === "permanent_ban");
    setOverride(false);
    setEffectiveTo("");
  }, [open, actionType]);

  const needsDual =
    action === "approve" || action === "activate"
      ? actionType === "permanent_ban" || highImpact
      : false;
  const needsSuper = override;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={action ? `Enforcement ${formatLabel(action)}` : "Enforcement action"}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={
              action === "reverse" || action === "activate" ? "danger" : "primary"
            }
            loading={busy}
            disabled={
              reason.trim().length < 3 ||
              ((needsDual || needsSuper) && approvalId.trim().length === 0)
            }
            onClick={() =>
              void onSubmit({
                reason: reason.trim(),
                ...(approvalId.trim()
                  ? { approval_id: approvalId.trim() }
                  : {}),
                ...(action === "approve" || action === "activate"
                  ? { high_impact: highImpact }
                  : {}),
                ...(override ? { high_risk_policy_override: true } : {}),
                ...(effectiveTo.trim()
                  ? { effective_to: toIsoUtc(effectiveTo) }
                  : {}),
              })
            }
          >
            Confirm
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Permanent ban and high-impact enforcement require Dual Approval.
          High-risk policy override requires Super Admin. Requester and reviewer
          must remain independent.
        </Text>
        {(action === "approve" || action === "activate") && (
          <Checkbox
            label="High-impact (Dual Approval)"
            checked={highImpact}
            onChange={(event) => setHighImpact(event.target.checked)}
          />
        )}
        <Checkbox
          label="High-risk policy override (Super Admin)"
          checked={override}
          onChange={(event) => setOverride(event.target.checked)}
        />
        <ApprovalPickerField
          label={
            needsDual
              ? "Dual approved Approval (required)"
              : needsSuper
                ? "Super Admin approved Approval (required)"
                : "Approved Approval (when required)"
          }
          value={approvalId}
          onChange={setApprovalId}
          required={needsDual || needsSuper}
          minAuthority={
            needsSuper ? "super_admin" : needsDual ? "dual" : undefined
          }
        />
        <Input
          label="Effective to (optional)"
          type="datetime-local"
          value={effectiveTo}
          onChange={(event) => setEffectiveTo(event.target.value)}
        />
        <Textarea
          label="Reason / rationale"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        {error ? (
          <Alert tone="danger" title="Action failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function CreateNoteDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    body_reference: string;
    classification?: DataClassification;
    support_ticket_id?: string | null;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [body, setBody] = useState("");
  const [classification, setClassification] =
    useState<DataClassification>("internal");
  const [ticketId, setTicketId] = useState("");

  useEffect(() => {
    if (!open) return;
    setBody("");
    setClassification("internal");
    setTicketId("");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add internal note"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={body.trim().length === 0}
            onClick={() =>
              void onSubmit({
                body_reference: body.trim(),
                classification,
                ...(ticketId.trim()
                  ? { support_ticket_id: ticketId.trim() }
                  : {}),
              })
            }
          >
            Add note
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Internal notes are case work product. Linking a Support Ticket ID does
          not merge Support into Moderation.
        </Text>
        <Textarea
          label="Body reference"
          value={body}
          onChange={(event) => setBody(event.target.value)}
        />
        <Select
          label="Classification"
          value={classification}
          onChange={(event) =>
            setClassification(event.target.value as DataClassification)
          }
          options={CLASSIFICATIONS.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        <Input
          label="Support Ticket ID (optional link)"
          value={ticketId}
          onChange={(event) => setTicketId(event.target.value)}
        />
        {error ? (
          <Alert tone="danger" title="Create failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function SubmitAppealDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    source_type: AppealSourceType;
    source_id: string;
    decision_reason?: string | null;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [sourceType, setSourceType] =
    useState<AppealSourceType>("enforcement_action");
  const [sourceId, setSourceId] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!open) return;
    setSourceType("enforcement_action");
    setSourceId("");
    setReason("");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Submit Appeal"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={sourceId.trim().length === 0}
            onClick={() =>
              void onSubmit({
                source_type: sourceType,
                source_id: sourceId.trim(),
                ...(reason.trim() ? { decision_reason: reason.trim() } : {}),
              })
            }
          >
            Submit
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Appeals are an independent review route from the original high-impact
          decision. Reviewer independence is enforced for enforcement sources.
        </Text>
        <Select
          label="Source type"
          value={sourceType}
          onChange={(event) =>
            setSourceType(event.target.value as AppealSourceType)
          }
          options={APPEAL_SOURCES.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        <Input
          label="Source ID"
          value={sourceId}
          onChange={(event) => setSourceId(event.target.value)}
        />
        <Textarea
          label="Notes (optional)"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        {error ? (
          <Alert tone="danger" title="Submit failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}
